import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { getExternalDestinationPermissions } from '../utils/permissionHelpers';
import { ExternalDestination } from '../types/inventory';

type CreateExternalDestinationInput = {
  name: string;
  contactName?: string;
  phone?: string;
  address?: string;
  notes?: string;
};

type UpdateExternalDestinationInput = {
  id: string;
  name: string;
  contactName?: string;
  phone?: string;
  address?: string;
  notes?: string;
};

export const useExternalDestinations = (options?: { includeInactive?: boolean }) => {
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const permissions = usePermissions();
  const perms = getExternalDestinationPermissions(permissions);
  const includeInactive = options?.includeInactive === true;

  return useQuery({
    queryKey: ['inventory', 'externalDestinations', businessId, includeInactive],
    queryFn: async () => {
      if (!businessId) return [];
      const q = query(
        collection(db, COLLECTIONS.externalDestinations),
        where('businessId', '==', businessId),
        orderBy('name', 'asc'),
        limit(300)
      );
      const snapshot = await getDocs(q);
      const allDestinations = snapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          businessId: data.businessId,
          name: data.name,
          contactName: data.contactName,
          phone: data.phone,
          address: data.address,
          notes: data.notes,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
          updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
        } as ExternalDestination;
      });
      if (includeInactive) {
        return allDestinations;
      }
      return allDestinations.filter((destination) => destination.isActive !== false);
    },
    enabled: !!businessId && perms.canView,
  });
};

export const useAddExternalDestination = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const permissions = usePermissions();
  const perms = getExternalDestinationPermissions(permissions);

  return useMutation({
    mutationFn: async (payload: CreateExternalDestinationInput) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }
      if (!perms.canCreate) {
        throw new Error('Vous n\'avez pas la permission de créer une destination externe');
      }
      if (!payload.name?.trim()) {
        throw new Error('Le nom de la destination est obligatoire');
      }

      const now = Timestamp.now();
      const destinationRef = await addDoc(collection(db, COLLECTIONS.externalDestinations), {
        businessId,
        name: payload.name.trim(),
        contactName: payload.contactName?.trim() || null,
        phone: payload.phone?.trim() || null,
        address: payload.address?.trim() || null,
        notes: payload.notes?.trim() || null,
        isActive: true,
        createdAt: now,
        updatedAt: now,
      });

      return destinationRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'externalDestinations', businessId] });
    },
  });
};

export const useUpdateExternalDestination = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const permissions = usePermissions();
  const perms = getExternalDestinationPermissions(permissions);

  return useMutation({
    mutationFn: async (payload: UpdateExternalDestinationInput) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }
      if (!perms.canUpdate) {
        throw new Error('Vous n\'avez pas la permission de modifier une destination externe');
      }
      if (!payload.name?.trim()) {
        throw new Error('Le nom de la destination est obligatoire');
      }

      const destinationRef = doc(db, COLLECTIONS.externalDestinations, payload.id);
      await updateDoc(destinationRef, {
        name: payload.name.trim(),
        contactName: payload.contactName?.trim() || null,
        phone: payload.phone?.trim() || null,
        address: payload.address?.trim() || null,
        notes: payload.notes?.trim() || null,
        updatedAt: Timestamp.now(),
      });
      return payload.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'externalDestinations', businessId] });
    },
  });
};

export const useSetExternalDestinationActive = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const permissions = usePermissions();
  const perms = getExternalDestinationPermissions(permissions);

  return useMutation({
    mutationFn: async (payload: { id: string; isActive: boolean }) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }
      if (!perms.canDelete) {
        throw new Error('Vous n\'avez pas la permission de désactiver/réactiver une destination externe');
      }

      const destinationRef = doc(db, COLLECTIONS.externalDestinations, payload.id);
      await updateDoc(destinationRef, {
        isActive: payload.isActive,
        updatedAt: Timestamp.now(),
      });
      return payload.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'externalDestinations', businessId] });
    },
  });
};
